import { PromiseWrapper } from "../utils/pw";
import { isValidURL } from "../utils/validateURL";
import uuid from "uuid-random";
import deepmerge from "deepmerge";
let FSBLOriginalPromise = Promise;
export const FSBLPromise = () => FSBLOriginalPromise;
export const FINSEMBLECONFIG = "$FINSEMBLECONFIG";
export const FINSEMBLECONFIGAPPS = "$FINSEMBLECONFIGAPPS";
export const FINSEMBLEMACROS = "$FINSEMBLEMACROS";
export const FINSEMBLECREDENTIALS = "$FINSEMBLECREDENTIALS";
export const FINSEMBLESERVICECONTEXT = "$FINSEMBLESERVICECONTEXT";
export const FINSEMBLEDIMENSIONMEMORY = "$FINSEMBLEDIMENSIONMEMORY";
export class Gate {
    constructor() {
        this.gatePromises = [];
    }
    async runOldest() {
        const entry = this.gatePromises[this.gatePromises.length - 1];
        const result = await entry.func();
        this.gatePromises = this.gatePromises.filter((x) => x !== entry);
        entry.resolve?.(result);
        if (this.gatePromises.length) {
            this.runOldest();
        }
    }
    async wait(func) {
        const entry = {
            func
        };
        entry.gatePromise = new Promise((resolve) => {
            entry.resolve = resolve;
        });
        this.gatePromises = this.gatePromises.concat(entry);
        if (this.gatePromises.length === 1) {
            this.runOldest();
        }
        return entry.gatePromise;
    }
}
export const safeInteropRegister = (handler, logger) => {
    return (args, caller) => {
        try {
            const result = handler(args, caller);
            if (result?.catch) {
                result.catch((e) => {
                    (logger ?? console).error("Unhandled exception or rejection in interop.register handler", e);
                });
            }
            return result;
        }
        catch (e) {
            (logger ?? console).error("Unhandled exception in interop.register handler", e);
            throw (e);
        }
    };
};
export const asyncFlatMap = async (inputs, func) => {
    const promises = inputs.map((input) => func(input));
    const results = await FSBLPromise().allSettled(promises);
    const rejects = results.filter((result) => result.status === "rejected");
    if (rejects.length) {
        throw rejects.map((r) => r.reason);
    }
    return results.flatMap((result) => result.status === "fulfilled" ? result.value : []);
};
export const asyncMap = async (inputs, func) => {
    const promises = inputs.map((input) => func(input));
    const results = await FSBLPromise().allSettled(promises);
    const rejects = results.filter((result) => result.status === "rejected");
    if (rejects.length) {
        throw rejects.map((r) => r.reason);
    }
    return results.flatMap((result) => result.status === "fulfilled" ? [result.value] : []);
};
export const asyncMapSequential = async (inputs, func) => {
    const results = [];
    for (let i = 0; i < inputs.length; i++) {
        try {
            const value = await func(inputs[i]);
            results.push({
                status: "fulfilled",
                value
            });
        }
        catch (e) {
            results.push({
                status: "rejected",
                reason: e.toString()
            });
        }
    }
    return results.flatMap((result) => result.status === "fulfilled" ? [result.value] : []);
};
export const asyncForEach = async (inputs, func) => {
    const promises = inputs.map((input) => func(input));
    const results = await Promise.allSettled(promises);
    const rejects = results.filter((result) => result.status === "rejected");
    if (rejects.length) {
        throw rejects.map((r) => r.reason);
    }
    return results;
};
export const convertInvokeResultToGoLang = (result, cb) => {
    const invokeError = result.all_errors ? result.all_errors.join(" ") : null;
    const response = result.returned;
    const { data = undefined, err = invokeError } = response;
    cb?.(err, data);
    return {
        err,
        data
    };
};
export const getMyWindowIdentifier = async (glue) => {
    const w = await glue.windows?.my();
    return {
        windowName: w?.id ?? "Unknown Window: running in node?",
        componentType: w?.name
    };
};
const ignoreLayouts = (w, configs) => {
    if (w.settings.ignoreFromLayouts === false || w.settings.ignoreFromLayouts === true) {
        return w.settings.ignoreFromLayouts;
    }
    const config = configs.find((config) => config.name === w.name);
    return config ? (config?.ignoreFromLayouts ?? false) : true;
};
const windowsInLayout = async (glue, windowList) => {
    const allWindows = await glue.windows.list();
    const configs = await glue.appManager.getConfigurations(allWindows.filter((w) => w.application).map((w) => w.application.name));
    return allWindows.filter((w) => {
        if (!windowList) {
            return w.isVisible && !ignoreLayouts(w, configs);
        }
        return windowList.includes(w.id);
    });
};
export const reveal = async (glue, params, cb) => {
    await asyncForEach(await windowsInLayout(glue, params?.windowList), async (w) => {
        if (w.state === "minimized") {
            await w.restore();
        }
        else {
            await w.focus();
        }
    });
    cb?.(null);
    return {
        err: null
    };
};
export const minimizeAll = async (glue, params, cb) => {
    await asyncForEach(await windowsInLayout(glue, params?.windowList), async (w) => {
        await w.minimize();
    });
    cb?.(null);
    return {
        err: null
    };
};
export const adjustValueForPixelRatio = (glue, value) => {
    if (typeof value !== "number") {
        return value;
    }
    const { scale } = glue.windows.my()?.screen ?? {
        scale: 1
    };
    const adjustment = (globalThis?.devicePixelRatio ?? 1) * scale;
    return Math.round(value * adjustment);
};
export const adjustBoundsFinsembleToGlue = (glue, bounds) => {
    const { scale } = glue.windows.my()?.screen ?? {
        scale: 1
    };
    const adjustment = (globalThis?.devicePixelRatio ?? 1) * scale;
    const obj = {
        snapped: false
    };
    if (typeof (bounds.left) === "number") {
        obj.left = bounds.left * adjustment;
    }
    if (typeof (bounds.top) === "number") {
        obj.top = bounds.top * adjustment;
    }
    if (typeof (bounds.right) === "number") {
        obj.right = bounds.right * adjustment;
    }
    if (typeof (bounds.bottom) === "number") {
        obj.bottom = bounds.bottom * adjustment;
    }
    if (typeof (bounds.width) === "number") {
        obj.width = bounds.width * adjustment;
    }
    if (typeof (bounds.height) === "number") {
        obj.height = bounds.height * adjustment;
    }
    return obj;
};
export const objectFlatMap = (obj, func) => Object.fromEntries(Object.entries(obj).flatMap(([k, v]) => func(k, v)));
export const reverseBoolean = (value) => {
    if (value === false) {
        return true;
    }
    if (value === true) {
        return false;
    }
    return value ?? undefined;
};
export const toGoLang = async (fc, cb) => {
    try {
        const data = await fc();
        cb?.(null, data);
        return { err: null, data };
    }
    catch (e) {
        const err = e instanceof Error ? e.toString() : e.message ?? JSON.stringify(e);
        cb?.(err);
        return { err };
    }
};
export const toGoLangInvoke = async (fc, cb) => {
    const { err, data } = await toGoLang(fc);
    if (err || !data) {
        cb?.(err);
        return { err };
    }
    const invokeError = data.all_errors ? data.all_errors.join(" ") : null;
    if (invokeError) {
        cb?.(invokeError);
        return { err: invokeError };
    }
    const response = data.returned;
    if (Object.prototype.hasOwnProperty.call(response, "err") || Object.prototype.hasOwnProperty.call(response, "data")) {
        cb?.(response.err ?? null, response.data);
        return { err: response.err ?? null, data: response.data };
    }
    cb?.(null, response);
    return { data: response, invocationResult: data };
};
export const nonNullish = (value) => value !== null && value !== undefined;
export const nullish = (value) => value === null || value === undefined;
export const deleteNullish = (obj) => {
    Object.keys(obj).forEach((key) => {
        if (obj[key] === null || obj[key] === undefined) {
            delete obj[key];
        }
    });
    return obj;
};
export const getPlacementSettings = (glue, params, gdDisplayIndex, config) => {
    const hasAnyPlacementOption = params?.left ?? params?.right ?? params?.top ?? params?.bottom ?? params?.width ?? params?.height ?? params?.monitor;
    if (hasAnyPlacementOption === undefined || hasAnyPlacementOption === null) {
        return undefined;
    }
    const left = params?.left ?? params?.options?.left;
    const top = params?.top ?? params?.options?.top;
    const right = params?.right ?? params?.options?.right;
    const bottom = params?.bottom ?? params?.options?.bottom;
    const width = params?.width ?? params?.options?.width;
    const height = params?.height ?? params?.options?.height;
    const placement = {
        snapped: config?.details?.placement?.snapped ?? false,
        left,
        top,
        right,
        bottom,
        width,
        height,
        display: gdDisplayIndex ?? config?.details?.placement?.display
    };
    adjustCenterAlignment(placement, params, config?.details?.placement);
    if (placement.width === "") {
        delete placement.width;
    }
    if (placement.height === "") {
        delete placement.height;
    }
    if (placement.height === 0 || placement.height === "0") {
        placement.height = 1;
    }
    if (placement.width === 0 || placement.width === "0") {
        placement.width = 1;
    }
    return deleteNullish(placement);
};
export const getCascade = (params) => {
    const staggerPixels = params?.staggerPixels;
    return {
        enabled: true,
        offset: staggerPixels ?? 50
    };
};
export const adjustCenterAlignment = (placement, params, configPlacement) => {
    if (placement.top === "center") {
        placement.verticalAlignment = "center";
    }
    if (placement.left === "center") {
        placement.horizontalAlignment = "center";
    }
    if (params?.dockable && params?.docked && params.dockable.some((item) => item === params.docked)) {
        placement.snapped = true;
        placement.height = params.dockedHeight ?? placement.height;
        placement.verticalAlignment = params.docked === "bottom" ? "bottom" : "top";
        placement.horizontalAlignment = "stretch";
    }
    if (!placement.horizontalAlignment && nonNullish(placement.right)) {
        placement.horizontalAlignment = "right";
    }
    if (!placement.horizontalAlignment && nonNullish(placement.left)) {
        placement.horizontalAlignment = "left";
    }
    if (!placement.verticalAlignment && nonNullish(placement.bottom)) {
        placement.verticalAlignment = "bottom";
    }
    if (!placement.verticalAlignment && nonNullish(placement.top)) {
        placement.verticalAlignment = "top";
    }
    if (!placement.horizontalAlignment) {
        placement.horizontalAlignment = configPlacement?.horizontalAlignment ?? "left";
    }
    if (!placement.verticalAlignment) {
        placement.verticalAlignment = configPlacement?.verticalAlignment ?? "top";
    }
};
export const getGdDisplayIndexWithMine = async (glue, monitor) => {
    if (!monitor || monitor === "mine") {
        const windowId = glue.windows.my()?.id;
        const gdDisplay = windowId ? await glue.displays.getByWindowId(windowId) : undefined;
        const isPrimary = gdDisplay?.isPrimary ?? true;
        return isPrimary ? "main" : gdDisplay?.index.toString();
    }
    return getGdDisplayIndex(monitor);
};
export const adjustPlacementBounds = async (glue, placement, myMonitorIndex) => {
    const displays = await glue.displays.all();
    const requestedDisplay = placement.display;
    const display = displays.find((d) => {
        if (d.index === Number(requestedDisplay))
            return true;
        if (requestedDisplay === "current" && d.index === Number(myMonitorIndex))
            return true;
        if (typeof (requestedDisplay) === "object" && requestedDisplay.index === d.index)
            return true;
        if (requestedDisplay === "main" && d.isPrimary)
            return true;
        return false;
    });
    const primaryDisplay = displays.find((d) => d.isPrimary);
    const workAreaTop = display ? display.workArea.top : 0;
    const workAreaLeft = display ? display.workArea.left : 0;
    const monitorAreaTop = display ? display.bounds.top : 0;
    const monitorAreaLeft = display ? display.bounds.left : 0;
    const scaleFactor = (display?.scaleFactor ?? 1) / (primaryDisplay?.scaleFactor ?? 1);
    if (typeof (placement.left) === "number") {
        placement.left -= (workAreaLeft - monitorAreaLeft) / scaleFactor;
        placement.left = placement.left / scaleFactor;
    }
    if (typeof (placement.top) === "number") {
        placement.top -= (workAreaTop - monitorAreaTop) / scaleFactor;
        placement.top = placement.top / scaleFactor;
    }
    if (placement.display && typeof (placement.display) !== "object") {
        placement.display = (parseInt(placement.display) + 1).toString();
    }
};
export const getGdDisplayIndex = (monitor) => {
    if (typeof monitor === "number") {
        const gdMonitor = monitor + 1;
        return gdMonitor.toString();
    }
    return "main";
};
export const getRelativeSettings = (glue, params) => {
    const adjacent = "adjacent";
    const relativeSettings = {};
    const left = params?.left ?? params?.options?.left;
    const isRelativeToRight = left === adjacent;
    if (isRelativeToRight) {
        relativeSettings.relativeDirection = "right";
    }
    const top = params?.top ?? params?.options?.top;
    const isRelativeToBottom = top === adjacent;
    if (isRelativeToBottom) {
        relativeSettings.relativeDirection = "bottom";
    }
    const right = params?.right ?? params?.options?.right;
    const isRelativeToLeft = right === adjacent;
    if (isRelativeToLeft) {
        relativeSettings.relativeDirection = "left";
    }
    const bottom = params?.bottom ?? params?.options?.bottom;
    const isRelativeToTop = bottom === adjacent;
    if (isRelativeToTop) {
        relativeSettings.relativeDirection = "top";
    }
    const hasRelativeDirection = Object.keys(relativeSettings).length > 0;
    if (hasRelativeDirection) {
        relativeSettings.relativeTo = params?.relativeWindow?.windowName ?? glue.windows.my()?.id;
    }
    return relativeSettings;
};
export const getComponentState = async (params, gdwin) => {
    const context = await gdwin?.getContext();
    const componentState = context?.["#finsemble-componentState"] ?? {};
    let result;
    if (params?.field) {
        result = componentState[params.field];
    }
    else if (params?.fields) {
        result = {};
        for (let i = 0; i < params.fields.length; i++) {
            result[params.fields[i]] = componentState[params.fields[i]];
        }
    }
    else {
        result = {
            ...componentState
        };
    }
    return {
        err: null,
        data: result
    };
};
export const formGroup = async (glue, windowId) => {
    const allWindows = glue.windows.list();
    const myBounds = await glue.windows.findById(windowId)?.getBounds();
    if (!myBounds) {
        console.log("formGroup() called on invalid window?", windowId);
        return;
    }
    myBounds.right = myBounds.left + myBounds.width;
    myBounds.bottom = myBounds.top + myBounds.height;
    const isTouching = (targetBounds) => {
        targetBounds.right = targetBounds.left + targetBounds.width;
        targetBounds.bottom = targetBounds.top + targetBounds.height;
        if (myBounds.left > targetBounds.right)
            return null;
        if (myBounds.right < targetBounds.left)
            return null;
        if (myBounds.top > targetBounds.bottom)
            return null;
        if (myBounds.bottom < targetBounds.top)
            return null;
        const distancesRaw = [
            {
                direction: "left",
                distance: targetBounds.right - myBounds.left
            },
            {
                direction: "right",
                distance: myBounds.right - targetBounds.left
            },
            {
                direction: "top",
                distance: targetBounds.bottom - myBounds.top
            },
            {
                direction: "bottom",
                distance: myBounds.bottom - targetBounds.top
            },
        ];
        const distances = distancesRaw.flatMap((item) => item.distance < 0 ? [] : item);
        if (!distances.length)
            return null;
        const sorted = distances.sort((lhs, rhs) => {
            if (lhs.distance > rhs.distance)
                return 1;
            if (rhs.distance > lhs.distance)
                return -1;
            return 0;
        });
        return sorted[0].direction;
    };
    for (let i = 0; i < allWindows.length; i++) {
        const targetWindow = allWindows[i];
        if (targetWindow.id === windowId)
            continue;
        if (!targetWindow.isVisible)
            continue;
        if (!targetWindow.isSticky)
            continue;
        const direction = isTouching(targetWindow.bounds);
        if (direction) {
            console.log(targetWindow.name, "snap to", glue.windows.findById(windowId)?.name, " ", direction);
            targetWindow.snap(windowId, direction);
            break;
        }
    }
};
export const setComponentState = async (params, gdwin) => {
    if (!params?.field && !params?.fields) {
        return { err: null };
    }
    let result = {};
    try {
        const context = await gdwin?.getContext();
        const currentState = context?.["#finsemble-componentState"] ?? {};
        if (params.field) {
            result = Object.assign(currentState, { [params.field]: params.value });
        }
        else if (params.fields) {
            result = Object.assign(currentState, ...params.fields.map(({ field, value }) => ({ [field]: value })));
        }
        await gdwin?.updateContext({ "#finsemble-componentState": result });
    }
    catch (e) {
        return { err: e.toString() };
    }
    return { err: null };
};
export const removeComponentState = async (params, gdwin) => {
    if (!params?.field && !params?.fields) {
        return { err: null };
    }
    const context = await gdwin?.getContext();
    const componentState = context?.["#finsemble-componentState"];
    if (!componentState) {
        return { err: null };
    }
    if (params.field) {
        delete componentState?.[params.field];
    }
    else if (params.fields) {
        params.fields.forEach((field) => {
            delete componentState[field?.field];
        });
    }
    gdwin?.updateContext({ "#finsemble-componentState": componentState });
    return { err: null };
};
export const openUrlWithBrowser = async (glue, url) => {
    if (!isValidURL(url)) {
        const err = `Failed to open browser - invalid url provided: '${url}'`;
        return { err };
    }
    try {
        await glue.appManager.application("open-browser")?.start({ url });
    }
    catch (err) {
    }
    return {};
};
export const updateNativeCmdArgs = (args, appId) => {
    return args.concat(` "componentType=${appId ?? "Unknown"}"`, ` "iocdGatewayAddress=${globalThis.glue42gd.gwURL}"`, " hostType=iocd");
};
export const fetchWindowOptions = async (w) => {
    const appConfig = await getConfigurationAndCache(w);
    const context = await w?.getContext();
    return {
        componentType: w?.name,
        resizable: appConfig?.details.hasSizeAreas ?? true,
        customData: {
            component: {
                type: w?.name
            },
            interop: {
                useLinker: appConfig?.details.channelSelector?.enabled ?? true
            },
            appConfig: appConfig?.customProperties?.appd,
            custom: appConfig?.customProperties?.appd?.hostManifests?.Finsemble?.custom,
            isAnApp: appConfig?.customProperties?.appd ? true : false,
            spawnData: context?.["#finsemble-spawnData"]
        }
    };
};
const adjustPlacementToBeOnSameMonitor = async (glue, placement, parentWindowName, position) => {
    if (position === "absolute" || position === "virtual") {
        return placement;
    }
    const parentWindow = parentWindowName ? glue.windows.findById(parentWindowName) : glue.windows.my();
    const parentDisplay = await parentWindow?.getDisplay();
    if (!parentWindow || !parentDisplay) {
        return placement;
    }
    if (position === "monitor" || !position) {
        placement = placement ?? {
            snapped: false
        };
        placement.display = parentDisplay.index + 1;
        return placement;
    }
    const offsets = {
        relative: parentWindow.bounds,
        available: parentDisplay.workArea
    };
    if (Object.hasOwnProperty.call(offsets, position) && placement) {
        const { left, top, height, width } = offsets[position];
        if (Number.isInteger(placement.left) || Number.isInteger(placement.top)) {
            placement.left = (placement.left ?? 0) + left;
            placement.top = (placement.top ?? 0) + top;
        }
        if (placement.height?.toString().includes("%")) {
            placement.height = parseFloat(placement.height) / 100 * height;
        }
        if (placement.width?.toString().includes("%")) {
            placement.width = parseFloat(placement.width) / 100 * width;
        }
        placement.horizontalAlignment = "left";
        placement.verticalAlignment = "top";
    }
    return placement;
};
const appConfigCache = {};
const appConfigGate = new Gate();
export const getConfigurationAndCache = async (w) => {
    return await appConfigGate.wait(async () => {
        if (!w?.application?.name)
            return undefined;
        if (appConfigCache[w.application.name]) {
            return appConfigCache[w.application.name];
        }
        const config = await w.application.getConfiguration();
        if (config) {
            appConfigCache[w.application.name] = config;
        }
        return config;
    });
};
export const spawnParamsToStartOptions = async (glue, params, config) => {
    const spawnParams = params ?? {};
    const currentTabCollection = glue.windows.my()?.tabGroupId;
    const startOptions = {
        ignoreSavedLayout: true
    };
    const gdDisplayIndex = await getGdDisplayIndexWithMine(glue, params?.monitor);
    let placement = getPlacementSettings(glue, spawnParams, gdDisplayIndex, config);
    if (placement) {
        placement = await adjustPlacementToBeOnSameMonitor(glue, placement, spawnParams.relativeWindow?.windowName, params?.position);
    }
    startOptions.placement = placement;
    startOptions.cascade = getCascade(spawnParams);
    const relativeSettings = getRelativeSettings(glue, spawnParams);
    if (spawnParams.ephemeral) {
        startOptions.showInTaskbar = spawnParams.ephemeral;
        startOptions.onTop = true;
        startOptions.hasSizeAreas = false;
        startOptions.isSticky = false;
    }
    const finsembleConfig = config?.customProperties?.appd?.hostManifests?.Finsemble;
    const addToWorkspace = spawnParams.addToWorkspace ?? finsembleConfig?.window?.addToWorkspace ?? false;
    startOptions.ignoreFromLayouts = reverseBoolean(addToWorkspace);
    if (nonNullish(relativeSettings.relativeTo)) {
        startOptions.relativeTo = relativeSettings.relativeTo;
    }
    if (nonNullish(relativeSettings.relativeDirection)) {
        startOptions.relativeDirection = relativeSettings.relativeDirection;
    }
    if (nonNullish(spawnParams.canGroup)) {
        startOptions.isSticky = spawnParams.canGroup ?? undefined;
    }
    if (nonNullish(spawnParams.options?.showTaskbarIcon)) {
        startOptions.showInTaskbar = spawnParams.options?.showTaskbarIcon;
    }
    if (nonNullish(spawnParams.options?.resizable)) {
        startOptions.hasSizeAreas = spawnParams.options?.resizable;
    }
    if (nonNullish(spawnParams.options?.alwaysOnTop)) {
        startOptions.onTop = spawnParams.options?.alwaysOnTop;
    }
    if (nonNullish(spawnParams.options?.autoShow)) {
        startOptions.hidden = reverseBoolean(spawnParams.options?.autoShow) ?? undefined;
    }
    if (nonNullish(spawnParams.minWidth)) {
        startOptions.minWidth = spawnParams.minWidth;
    }
    if (nonNullish(spawnParams.minHeight)) {
        startOptions.minHeight = spawnParams.minHeight;
    }
    if (nonNullish(spawnParams.maxWidth)) {
        startOptions.maxWidth = spawnParams.maxWidth;
    }
    if (nonNullish(spawnParams.maxHeight)) {
        startOptions.maxHeight = spawnParams.maxHeight;
    }
    if (nonNullish(spawnParams.slave)) {
        startOptions.isChild = spawnParams.slave;
    }
    if (nonNullish(spawnParams.options?.windowState)) {
        startOptions.windowState = spawnParams.options?.windowState;
    }
    if (nonNullish(spawnParams.frame)) {
        startOptions.showTitleBar = reverseBoolean(spawnParams.frame) ?? undefined;
    }
    if (spawnParams.addToTabCollection) {
        startOptions.tabGroupId = typeof spawnParams.addToTabCollection === "string" ? spawnParams.addToTabCollection : currentTabCollection;
    }
    if (spawnParams.options?.preloadScripts) {
        startOptions.preloadScripts = spawnParams.options.preloadScripts;
    }
    if (spawnParams.url) {
        startOptions.url = spawnParams.url;
    }
    if (spawnParams.path) {
        const splits = spawnParams.path.split(/[\\/]/);
        const command = splits.pop();
        const path = splits.join("/");
        startOptions.command = command;
        startOptions.path = path;
    }
    if (nonNullish(spawnParams.options?.windowSpawnTimeout)) {
        startOptions.timeout = spawnParams.options?.windowSpawnTimeout;
    }
    const argumentsAsQueryString = spawnParams.argumentsAsQueryString ?? config.customProperties?.appd?.hostManifests?.Finsemble?.window?.argumentsAsQueryString;
    if (nonNullish(spawnParams.arguments)) {
        if (argumentsAsQueryString) {
            startOptions.urlLoadOptions = {
                queryString: spawnParams.arguments
            };
        }
        else {
            startOptions.parameters = spawnParams.arguments;
        }
    }
    if (nonNullish(spawnParams.env)) {
        startOptions.env = spawnParams.env;
    }
    if (config.type === "exe" || "assimilation" === config?.customProperties?.appd?.hostManifests?.Finsemble?.window?.windowType) {
        let cmdArguments = startOptions.parameters ??
            config?.customProperties?.appd?.details?.arguments ??
            config?.customProperties?.appd?.hostManifests?.Finsemble?.window?.arguments ?? "";
        if (config.type === "exe" && "assimilation" !== config?.customProperties?.appd?.hostManifests?.Finsemble?.window?.windowType) {
            cmdArguments = updateNativeCmdArgs(cmdArguments, config.name);
            if (!cmdArguments.includes("apiToLogFile")) {
                const fullConfig = await glue.contexts.get(FINSEMBLECONFIG);
                if (fullConfig?.iocd?.apiToLogFile === true) {
                    cmdArguments = cmdArguments.concat(" apiToLogFile=true");
                }
            }
        }
        startOptions.parameters = cmdArguments;
    }
    if (nonNullish(startOptions.iocd)) {
        return deepmerge(startOptions, startOptions.iocd);
    }
    return startOptions;
};
export const traverse = (obj, iterator, _path = []) => {
    if (!obj || typeof obj !== "object") {
        return;
    }
    Object.entries(obj).forEach((entry) => {
        const [key, value] = entry;
        const newPath = _path.concat([key]);
        if (typeof value === "object") {
            traverse(value, iterator, newPath);
        }
        else {
            iterator({ _path: newPath, obj, key, value });
        }
    });
};
export class AsyncConstructors {
    constructor() {
        this.promises = [];
    }
    addConstructor() {
        const pw = new PromiseWrapper();
        this.promises.push(pw.promise);
        return pw;
    }
    finishConstructor(pw) {
        pw.resolve();
    }
    async ready() {
        await FSBLPromise().allSettled(this.promises);
    }
}
export const removeDups = (value, index, array) => array.indexOf(value) === index;
export const onlyMultiple = (value, index, array) => array.filter((item) => item === value).length > 1;
export const invertNonDestructive = (source) => {
    const getKeysForValue = (value) => Object.keys(source).filter((key) => source[key] === value);
    const values = Object.values(source).filter(removeDups);
    return Object.fromEntries(values.map((value) => [value, getKeysForValue(value)]));
};
const groupsFromComponents = (components) => {
    const groupIds = components.map((component) => component.state?.restoreSettings?.groupId).filter(nonNullish).filter(removeDups);
    const groupArray = groupIds.map((id) => {
        return [id, {
                isMovable: true,
                isAlwaysOnTop: false,
                windowNames: components.flatMap((component) => component.state?.restoreSettings?.groupId === id ? [component.state.instanceId] : [])
            }];
    }).filter((entry) => entry[1].windowNames.length > 1);
    return Object.fromEntries(groupArray);
};
const windowStateGlueToFinsemble = (windowState, restoreState) => {
    if (windowState === "Normal") {
        return 0;
    }
    if (windowState === "Minimized" && restoreState === "Normal") {
        return 1;
    }
    if (windowState === "Minimized" && restoreState === "Maximized") {
        return 3;
    }
    if (windowState === "Maximized") {
        return 2;
    }
    return 0;
};
const windowStateFinsembleToGlue = (windowState) => {
    if (windowState === 0) {
        return { windowState: "Normal", restoreState: "Normal" };
    }
    if (windowState === 1) {
        return { windowState: "Minimized", restoreState: "Normal" };
    }
    if (windowState === 3) {
        return { windowState: "Minimized", restoreState: "Maximized" };
    }
    if (windowState === 2) {
        return { windowState: "Maximized", restoreState: "Normal" };
    }
    return { windowState: "Normal", restoreState: "Normal" };
};
const filterAndConvertFDC3Contexts = (context) => {
    const fdc3Contexts = {};
    Object.keys(context).forEach((key) => {
        const contextType = key.split("#finsemble-persistedContext-")[1];
        if (!contextType)
            return;
        fdc3Contexts[contextType] = context[key];
        delete context[key];
    });
    context["Interop-#Workspace-contexts"] = fdc3Contexts;
};
const finsembleComponentStatesFromComponents = (components) => {
    const entries = components.map((component) => {
        const componentType = component.application;
        const key = `${componentType}${component.state.instanceId}`;
        const channels = channelsGlueToFinsemble(component.state.channelId);
        const linker = component.state.channelId ? {
            "Interop-#Linker-linkedChannels": {
                channels
            }
        } : {};
        const componentStates = {
            ...component.state?.context ?? {},
            ...component.state?.context?.["#finsemble-componentState"],
            ...linker
        };
        delete componentStates["#finsemble-componentState"];
        const restrictions = component.state.channelRestrictions?.channels ?? [];
        componentStates["customFDC3-channels"] = Object.fromEntries(channels.map((channel) => {
            const restriction = restrictions.find((r) => r.name === channel);
            if (restriction && !restriction.read)
                return [channel, "Broadcast"];
            if (restriction && !restriction.write)
                return [channel, "Listen"];
            return [channel, "Both"];
        }));
        if (component.state.userTitle) {
            componentStates["persistedTitle"] = component.state.userTitle;
        }
        filterAndConvertFDC3Contexts(componentStates);
        return [key, componentStates];
    });
    return Object.fromEntries(entries);
};
const finsembleWindowDataFromComponents = (components) => {
    const windowDatas = components.map((component) => {
        const componentType = component.application;
        return {
            componentType,
            name: `${componentType}${component.state.instanceId}`,
            opacity: 1,
            windowState: windowStateGlueToFinsemble(component.state.windowState, component.state.restoreState),
            left: component.state?.bounds?.left,
            top: component.state?.bounds?.top,
            width: component.state?.bounds?.width,
            height: component.state?.bounds?.height,
            right: component.state?.bounds?.right,
            bottom: component.state?.bounds?.bottom,
            defaultLeft: component.state?.bounds?.left,
            defaultTop: component.state?.bounds?.top,
            defaultWidth: component.state?.bounds?.width,
            defaultHeight: component.state?.bounds?.height,
            workspaces: component.state.workspaces,
            type: component.type,
        };
    });
    const tabGroupIds = components.map((component) => component.state.tabGroupId).filter(nonNullish).filter(onlyMultiple).filter(removeDups);
    const tabOrder = (lhs, rhs) => {
        if (Math.abs(lhs.state.tabIndex) > Math.abs(rhs.state.tabIndex)) {
            return 1;
        }
        if (Math.abs(lhs.state.tabIndex) < Math.abs(rhs.state.tabIndex)) {
            return -1;
        }
        return 0;
    };
    const stacks = tabGroupIds.map((id) => {
        const visibleComponent = components.find((component) => component.state.tabGroupId === id && component.state.tabIndex < 0);
        const childWindowIdentifiers = components.filter((component) => component.state.tabGroupId === id).sort(tabOrder).map((component) => {
            return {
                windowName: `${component.application}${component.state.instanceId}`
            };
        });
        return {
            name: id,
            windowType: "StackedWindow",
            componentType: "StackedWindow",
            visibleWindowIdentifier: {
                windowName: visibleComponent ? `${visibleComponent.application}${visibleComponent.state.instanceId}` : undefined
            },
            childWindowIdentifiers,
            left: visibleComponent?.state.bounds.left,
            top: visibleComponent?.state.bounds.top,
            width: visibleComponent?.state.bounds.width,
            height: visibleComponent?.state.bounds.height,
            bounds: {
                left: visibleComponent?.state.bounds.left,
                top: visibleComponent?.state.bounds.top,
                width: visibleComponent?.state.bounds.width,
                height: visibleComponent?.state.bounds.height
            }
        };
    });
    return { windowData: windowDatas.concat(stacks), stacks };
};
export const getImportStatus = (name, result) => result.failed.find((result) => result.name === name) ? "failed" : "success";
const findStackForWindow = (workspace, windowData) => {
    const stacks = workspace.windowData?.filter((w) => w.windowType === "StackedWindow");
    const stack = stacks?.find((s) => s.childWindowIdentifiers?.some((w) => w.windowName === windowData.name));
    return stack ?? null;
};
export const glueWindowToWindowDescriptor = (win, configs) => {
    const config = configs.find((c) => c.name === win.name);
    return {
        name: win.id,
        defaultLeft: win.bounds.left,
        defaultTop: win.bounds.top,
        defaultBottom: win.bounds.top + win.bounds.height,
        defaultWidth: win.bounds.width,
        defaultHeight: win.bounds.height,
        defaultRight: win.bounds.left + win.bounds.width,
        componentType: win.name,
        customData: {
            isAnApp: win.isSticky,
            appConfig: {
                appId: win.name,
                component: win.name
            },
            spawnData: win.context?.["#finsemble-spawnData"],
            preload: config?.customProperties?.appd?.hostManifests?.Finsemble?.component?.preload ?? []
        },
        left: win.bounds.left,
        top: win.bounds.top,
        right: win.bounds.left + win.bounds.width,
        bottom: win.bounds.top + win.bounds.height,
        width: win.bounds.width,
        height: win.bounds.height
    };
};
export const glueDefinitionToComponent = (definition) => {
    const icon = (definition.icon && definition.icon !== "") ? {
        imageType: "url",
        path: definition.icon
    } : undefined;
    return {
        window: {},
        component: {
            displayName: definition.title,
            type: definition.name,
            isUserDefined: definition.customProperties?.["isUserDefined"] ? true : false
        },
        foreign: {
            components: {
                "App Launcher": {
                    launchableByUser: reverseBoolean(definition.hidden) ?? true
                }
            }
        },
        icons: {
            toolbarIcon: icon,
            taskbarIcon: icon,
            titleBarIcon: icon,
        }
    };
};
export const glueDefinitionToAppD = (config) => {
    const componentConfig = glueDefinitionToComponent(config);
    return {
        appId: config.name,
        type: config.type === "exe" ? "native" : "web",
        hostManifests: {
            Finsemble: componentConfig
        },
        details: config.details.url ? {
            url: config.details.url
        } : {
            path: config.details.command
        }
    };
};
export const getComponentList = async (glue) => {
    const { err, data: definitions } = await toGoLang(() => glue.appManager.getConfigurations());
    if (err || !definitions) {
        const error = `getComponentList:getConfigurations ${err}`;
        return { err: error };
    }
    const finsembleComponents = definitions.flatMap((definition) => {
        const rawComponent = definition?.customProperties?.component ?? definition?.customProperties?.appd?.hostManifests?.Finsemble;
        const componentRecord = deepmerge(rawComponent ?? {}, glueDefinitionToComponent(definition));
        const componentRecordWithAppConfig = deepmerge(componentRecord, { appConfig: definition?.customProperties?.appd });
        return [componentRecordWithAppConfig];
    });
    const componentList = Object.fromEntries(finsembleComponents.map((component) => [component?.component?.type ?? "Unknown Component", component]));
    return {
        err: null,
        data: componentList
    };
};
const getTabIndex = (workspace, windowData) => {
    const stack = findStackForWindow(workspace, windowData);
    const indexOf = stack?.childWindowIdentifiers?.findIndex((w) => w.windowName === windowData.name) ?? 0;
    const isActiveTabInStack = stack?.visibleWindowIdentifier?.windowName === windowData.name;
    const isActiveTab = stack ? isActiveTabInStack : true;
    return (indexOf + 1) * (isActiveTab ? -1 : 1);
};
const getTabGroup = (workspace, windowData) => {
    const stack = findStackForWindow(workspace, windowData);
    return stack?.name;
};
const getFinsembleLinkedChannels = (workspace, windowData) => {
    return workspace?.componentStates?.[windowData.name]?.["Interop-#Linker-linkedChannels"]?.channels ?? [];
};
const getGroupId = (workspace, windowData, stack) => {
    const isInGroup = (group) => {
        let inGroup = false;
        if (group.isMovable) {
            inGroup = (group.windowNames.includes(windowData.name) || group.windowNames.includes(stack ?? ""));
            if (!inGroup) {
                group.windowNames.forEach((windowName) => {
                    if (windowData.name.endsWith(windowName)) {
                        inGroup = true;
                    }
                });
            }
        }
        return inGroup;
    };
    const [groupId] = Object.entries(workspace.groups).find(([id, group]) => isInGroup(group)) ?? [null];
    return groupId;
};
const getContextsForComponent = (workspace, name) => {
    const context = workspace.componentStates?.[name] ?? {};
    const componentStates = {};
    Object.keys(context).forEach((key) => {
        if (key === "#finsemble-windowName")
            return;
        if (key === "#finsemble-spawnData")
            return;
        if (key === "Interop-#Linker-linkedChannels") {
            delete context[key];
            return;
        }
        if (key === "Interop-#Workspace-contexts") {
            const fdc3 = context[key];
            Object.entries(fdc3).forEach(([name, value]) => {
                context[`#finsemble-persistedContext-${name}`] = value;
            });
            delete context[key];
            return;
        }
        componentStates[key] = context[key];
        delete context[key];
    });
    context["#finsemble-componentState"] = componentStates;
    return context;
};
export const workspaceToLayout = (workspaceName, workspace, index) => {
    const components = (workspace.windowData ?? []).flatMap((windowData) => {
        if (windowData.windowType === "StackedWindow") {
            return [];
        }
        const channelId = channelsFinsembleToGlue(getFinsembleLinkedChannels(workspace, windowData));
        const windowState = windowStateFinsembleToGlue(windowData.windowState);
        const stack = findStackForWindow(workspace, windowData)?.name;
        const context = getContextsForComponent(workspace, windowData.name);
        const componentStates = workspace.componentStates?.[windowData.name] ?? {};
        const channelRestrictions = {
            channels: Object.entries(componentStates["customFDC3-channels"] ?? {}).flatMap(([name, value]) => {
                if (value === "Both")
                    return [{ name, read: true, write: true }];
                if (value === "Broadcast")
                    return [{ name, read: false, write: true }];
                if (value === "Listen")
                    return [{ name, read: true, write: false }];
                return [];
            })
        };
        const layoutComponent = {
            application: windowData.componentType,
            type: windowData.type ?? "window",
            componentType: "application",
            state: {
                context,
                isCollapsed: false,
                windowState: windowState.windowState,
                channelSelector: {
                    enabled: true
                },
                channelRestrictions,
                mode: "tab",
                channelId,
                restoreState: windowState.restoreState,
                bounds: {
                    left: windowData.left,
                    top: windowData.top,
                    width: windowData.width,
                    height: windowData.height
                },
                isSticky: true,
                instanceId: windowData.name,
            }
        };
        const workspaces = windowData.workspaces;
        if (workspaces) {
            layoutComponent.state.workspaces = workspaces;
        }
        const userTitle = componentStates["persistedTitle"] ?? null;
        if (userTitle) {
            layoutComponent.state.userTitle = userTitle;
        }
        const tabIndex = getTabIndex(workspace, windowData);
        const tabGroupId = getTabGroup(workspace, windowData);
        const groupId = getGroupId(workspace, windowData, stack) ?? `${tabGroupId}-1`;
        if (nonNullish(tabIndex)) {
            layoutComponent.state.tabIndex = tabIndex;
        }
        layoutComponent.state.tabGroupId = tabGroupId ?? uuid();
        layoutComponent.state.restoreSettings = {
            groupId,
            groupZOrder: 0
        };
        return [layoutComponent];
    });
    const metadata = { "iop#position": index };
    if (workspace.id) {
        metadata.id = workspace.id;
    }
    const layout = {
        name: workspaceName,
        type: "Global",
        components,
        context: {},
        metadata,
        version: 2
    };
    return layout;
};
const channelMap = {
    "Channel 1": "Purple",
    "Channel 2": "Yellow",
    "Channel 3": "Green",
    "Channel 4": "Red",
    "Channel 5": "Blue",
    "Channel 6": "Orange",
    "Channel 7": "Pink",
    "Channel 8": "Magenta",
    "Glue Extra 1": "Dark Yellow",
    "Glue Extra 2": "Lime",
    "Glue Extra 3": "Cyan"
};
export const channelsFinsembleToGlue = (channels) => {
    return channels.join("+++");
};
export const channelsGlueToFinsemble = (glueChannelId) => {
    if (glueChannelId === "")
        return [];
    const channels = glueChannelId.split("+++");
    return channels;
};
export const layoutToWorkspace = (layout) => {
    const groups = groupsFromComponents(layout.components);
    const { windowData, stacks } = finsembleWindowDataFromComponents(layout.components);
    const componentStates = finsembleComponentStatesFromComponents(layout.components);
    stacks.forEach((stack) => componentStates[stack.name] = {});
    const windows = windowData.map((w) => w.name);
    const workspace = {
        version: "1.0.0",
        name: layout.name,
        type: "workspace",
        groups,
        windows,
        componentStates: componentStates,
        custom: {},
        windowData: windowData,
        tabCollections: []
    };
    if (layout.metadata?.["id"]) {
        workspace.id = layout.metadata.id;
    }
    return workspace;
};
export const orderedLayouts = async (layouts) => {
    const byIopPosition = (lhs, rhs) => {
        const lhsp = lhs.metadata?.["iop#position"];
        const rhsp = rhs.metadata?.["iop#position"];
        if (lhsp === undefined && rhsp !== undefined) {
            return 1;
        }
        if (lhsp !== undefined && rhsp === undefined) {
            return -1;
        }
        if (lhsp === undefined && rhsp === undefined) {
            if (lhs.name < rhs.name) {
                return -1;
            }
            if (lhs.name > rhs.name) {
                return 1;
            }
            return 0;
        }
        if (lhsp < rhsp) {
            return -1;
        }
        if (lhsp > rhsp) {
            return 1;
        }
        return 0;
    };
    return layouts.sort(byIopPosition);
};
export const convertGlueMonitorBoundsToFinsemble = (glueMonitor) => {
    if (!glueMonitor?.workArea || !glueMonitor?.scaleFactor || !glueMonitor.bounds) {
        return;
    }
    const availableRect = {
        left: glueMonitor.workArea?.left * glueMonitor?.scaleFactor,
        top: glueMonitor.workArea.top * glueMonitor.scaleFactor,
        width: glueMonitor.workArea.width,
        height: glueMonitor.workArea.height,
    };
    availableRect.right = availableRect.left + availableRect.width;
    availableRect.bottom = availableRect.top + availableRect.height;
    const monitorRect = {
        left: glueMonitor.bounds.left * glueMonitor.scaleFactor,
        top: glueMonitor.bounds.top * glueMonitor.scaleFactor,
        width: glueMonitor.bounds.width,
        height: glueMonitor.bounds.height,
    };
    monitorRect.right = monitorRect.left + monitorRect.width;
    monitorRect.bottom = monitorRect.top + monitorRect.height;
    return { availableRect, monitorRect };
};
export const convertGetMonitorInfo = (glueMonitor) => {
    if (!glueMonitor?.workArea || !glueMonitor?.scaleFactor || !glueMonitor.bounds) {
        return null;
    }
    const { availableRect, monitorRect } = convertGlueMonitorBoundsToFinsemble(glueMonitor);
    return {
        monitor: {
            dipRect: availableRect,
            scaledRect: availableRect
        },
        availableRect: availableRect,
        monitorRect: monitorRect,
        name: `device${glueMonitor.index}`,
        deviceId: Number(glueMonitor.id ?? 0).toString(),
        deviceScaleFactor: 1 / glueMonitor.scaleFactor,
        displayDeviceActive: true,
        position: glueMonitor.index,
        whichMonitor: glueMonitor.isPrimary ? "primary" : glueMonitor.index
    };
};
const calculatePixel = (length, percentage) => {
    if (percentage.includes("%")) {
        const pct = parseFloat(percentage) / 100;
        return Math.abs(length * pct);
    }
    else {
        return parseInt(percentage);
    }
};
const maybeConvertPercentagesToPixels = (parentWindow, childWindow) => {
    const top = typeof childWindow.top === "string" ? calculatePixel(parentWindow.height, childWindow.top) : childWindow.top;
    const left = typeof childWindow.left === "string" ? calculatePixel(parentWindow.width, childWindow.left) : childWindow.left;
    const height = typeof childWindow.height === "string" ? calculatePixel(parentWindow.height, childWindow.height) : childWindow.height;
    const width = typeof childWindow.width === "string" ? calculatePixel(parentWindow.width, childWindow.width) : childWindow.width;
    return { top, left, width, height };
};
export const convertFinsembleLaunchGroupAppd2GlueGroupOptions = async (glue, appd) => {
    const title = appd.title ?? "Group";
    const { toSpawn } = appd.hostManifests?.Finsemble?.window ?? {};
    if (!toSpawn || !Array.isArray(toSpawn)) {
        return { err: `convertFinsembleLaunchGroupAppd2GlueGroupOptions: Unexpected value in appd hostManifests.Finsemble.window.toSpawn property. Expected array, received: ${toSpawn}` };
    }
    if (!glue.windows.my()) {
        return {};
    }
    const display = await glue.windows.my()?.getDisplay();
    if (!display) {
        return { err: "convertFinsembleLaunchGroupAppd2GlueGroupOptions: Can't get display" };
    }
    const parentBounds = await maybeConvertPercentagesToPixels(display?.bounds, appd.hostManifests?.Finsemble?.window);
    const frames = toSpawn.map((entry) => {
        const { top, left, height, width } = entry.spawnOptions ?? {};
        const bounds = maybeConvertPercentagesToPixels(parentBounds, { top, left, height, width });
        if ("components" in entry) {
            return {
                mode: "tab",
                bounds,
                applications: entry.components.map(({ componentType }) => {
                    return { name: componentType };
                })
            };
        }
        return {
            mode: "tab",
            bounds,
            applications: [{ name: entry.componentType }]
        };
    });
    return { data: {
            context: {},
            groups: [{
                    title,
                    frames
                }]
        } };
};
